/**
 * Win Rate Chart - Bot Performance Visualization
 * Shows historical win rate performance with trend analysis
 */

import { useMemo } from 'react';
import { Icons } from '../Icons';

interface WinRateChartProps {
  days?: number;
}

interface DayData {
  date: string;
  winRate: number;
  wins: number;
  losses: number;
  total: number;
}

export function WinRateChart({ days = 30 }: WinRateChartProps) {
  // Generate mock historical data
  const data = useMemo<DayData[]>(() => {
    const result: DayData[] = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);

      // Generate realistic win rate with some variance (60-80% range with trend)
      const baseWinRate = 68;
      const trend = (days - i) / days * 8; // Gradual improvement over time
      const variance = (Math.random() - 0.5) * 15; // ±7.5% daily variance
      const winRate = Math.min(95, Math.max(50, baseWinRate + trend + variance));

      // Generate trade counts (2-12 trades per day)
      const total = Math.floor(Math.random() * 10) + 2;
      const wins = Math.round(total * (winRate / 100));
      const losses = total - wins;

      result.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        winRate: Math.round(winRate),
        wins,
        losses,
        total
      });
    }

    return result;
  }, [days]);

  // Calculate statistics
  const stats = useMemo(() => {
    const avgWinRate = data.reduce((sum, d) => sum + d.winRate, 0) / data.length;
    const totalWins = data.reduce((sum, d) => sum + d.wins, 0);
    const totalLosses = data.reduce((sum, d) => sum + d.losses, 0);
    const totalTrades = totalWins + totalLosses;
    const bestDay = data.reduce((best, d) => d.winRate > best.winRate ? d : best, data[0]);
    const worstDay = data.reduce((worst, d) => d.winRate < worst.winRate ? d : worst, data[0]);

    return {
      avgWinRate: Math.round(avgWinRate),
      totalWins,
      totalLosses,
      totalTrades,
      bestDay,
      worstDay
    };
  }, [data]);

  // Chart dimensions
  const maxWinRate = 100;
  const chartHeight = 200;
  const chartWidth = 100; // percentage

  // Show last 14 days for better readability
  const displayData = data.slice(-14);

  return (
    <div>
      {/* Statistics Overview */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '12px',
        marginBottom: '24px'
      }}>
        <div style={{
          padding: '14px',
          background: 'var(--bg)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
            AVG WIN RATE
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
            {stats.avgWinRate}%
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            Last {days} days
          </div>
        </div>

        <div style={{
          padding: '14px',
          background: 'var(--bg)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
            TOTAL TRADES
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>
            {stats.totalTrades}
          </div>
          <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>
            {stats.totalWins}W / {stats.totalLosses}L
          </div>
        </div>

        <div style={{
          padding: '14px',
          background: 'var(--bg)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
            BEST DAY
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
            {stats.bestDay.winRate}%
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            {stats.bestDay.date}
          </div>
        </div>

        <div style={{
          padding: '14px',
          background: 'var(--bg)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
            CONSISTENCY
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
            {data.filter(d => d.winRate >= 65).length}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            Days above 65%
          </div>
        </div>
      </div>

      {/* Bar Chart */}
      <div style={{
        background: 'var(--bg)',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid var(--border)'
      }}>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
            Daily Win Rate (Last 14 Days)
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#10b981' }} />
              <span style={{ color: 'var(--muted)' }}>≥ 70%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#f59e0b' }} />
              <span style={{ color: 'var(--muted)' }}>60-69%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#ef4444' }} />
              <span style={{ color: 'var(--muted)' }}>&lt; 60%</span>
            </div>
          </div>
        </div>

        {/* Chart Area */}
        <div style={{
          position: 'relative',
          height: `${chartHeight}px`,
          display: 'flex',
          alignItems: 'flex-end',
          gap: '4px',
          paddingBottom: '24px'
        }}>
          {/* Average line */}
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${(stats.avgWinRate / maxWinRate) * chartHeight + 24}px`,
            height: '1px',
            background: 'rgba(139, 92, 246, 0.5)',
            borderTop: '1px dashed rgba(139, 92, 246, 0.5)',
            zIndex: 1,
            pointerEvents: 'none'
          }}>
            <span style={{
              position: 'absolute',
              right: '4px',
              top: '-10px',
              fontSize: '10px',
              color: 'rgba(139, 92, 246, 0.8)',
              fontWeight: 600,
              background: 'var(--bg)',
              padding: '0 4px'
            }}>
              Avg {stats.avgWinRate}%
            </span>
          </div>

          {/* Bars */}
          {displayData.map((day, index) => {
            const barHeight = (day.winRate / maxWinRate) * chartHeight;
            const barColor = day.winRate >= 70 ? '#10b981' : day.winRate >= 60 ? '#f59e0b' : '#ef4444';

            return (
              <div
                key={index}
                style={{
                  flex: 1,
                  height: `${barHeight}px`,
                  background: `linear-gradient(to top, ${barColor}, ${barColor}cc)`,
                  borderRadius: '4px 4px 0 0',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '20px'
                }}
                title={`${day.date}: ${day.winRate}% (${day.wins}W/${day.losses}L)`}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Value on hover */}
                <div style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: barColor,
                  whiteSpace: 'nowrap',
                  marginBottom: '4px',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  pointerEvents: 'none'
                }}
                  className="bar-value"
                >
                  {day.winRate}%
                </div>

                {/* Date label */}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%) rotate(-45deg)',
                  transformOrigin: 'top left',
                  fontSize: '10px',
                  color: 'var(--muted)',
                  whiteSpace: 'nowrap',
                  marginTop: '8px'
                }}>
                  {day.date}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Performance Insights */}
      <div style={{
        marginTop: '16px',
        padding: '14px',
        background: 'rgba(16, 185, 129, 0.1)',
        border: '1px solid rgba(16, 185, 129, 0.3)',
        borderRadius: '8px',
        fontSize: '13px',
        display: 'flex',
        gap: '10px'
      }}>
        <Icons.TrendingUp size={18} style={{ color: '#10b981', flexShrink: 0, marginTop: '1px' }} />
        <div>
          <strong style={{ color: '#10b981' }}>Strong Performance:</strong>{' '}
          <span style={{ color: 'var(--text)' }}>
            Your bot maintains a {stats.avgWinRate}% win rate over the last {days} days,
            significantly above the 60% industry benchmark. Keep your current strategy and risk parameters.
          </span>
        </div>
      </div>

      <style>{`
        .bar-value {
          opacity: 0 !important;
        }
        div:hover .bar-value {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
}
